import React, { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { isNullOrUndefined } from "utils/validations";
import { showAlertServiceError } from "utils/alerts";
import useForm from "hooks/useForm";

const FormsContext = React.createContext(undefined);

export function FormsContextProvider({ children }) {
  const { getFormsById } = useForm();
  const [infoCurrentform, setInfoCurrentForm] = useState({});
  const [isLoadingForms, setIsLoadingForms] = useState(false);

  /**
   * Fetches forms by their UUID and performs a specified action on the response.
   * This function sets a loading state while fetching data and updates the current form information
   * if the response contains valid data. If the response data is null or undefined, it triggers an
   * alert for a service error. The loading state is reset after the operation completes.
   * @param {string} formUuid - The unique identifier of the form to be fetched.
   * @param {Function} action - The action to be performed on the fetched form data.
   */
  const getFormsByIds = useCallback((formUuid, action) => {
    setIsLoadingForms(true);
    getFormsById(formUuid, action)
      .then((response) => {
        if (isNullOrUndefined(response.data) === false) {
          setInfoCurrentForm(response.data);
        } else {
          showAlertServiceError();
        }
      })
      .finally(() => setIsLoadingForms(false));
  }, []);

  return (
    <FormsContext.Provider value={{ getFormsByIds, infoCurrentform, isLoadingForms }}>
      {children}
    </FormsContext.Provider>
  );
}

FormsContextProvider.propTypes = {
  children: PropTypes.any.isRequired,
};
export function useFormsContext() {
  const context = React.useContext(FormsContext);
  if (isNullOrUndefined(context) === true) {
    throw new Error("useFormsContext must be inside the FormsContext provider.");
  }
  return context;
}
